'use client'

import { useCallback, useState } from 'react'

import { useOnMount } from '@src/hooks/useOnMount'
import { useLocale } from '@src/i18n/LocaleProvider'
import type { Consent, GetConsentsResponse } from '@src/services/consents.service'
import { ServerActions } from '@src/services/server-actions.service'

interface Props {
  fetchConsents?: boolean
}

export const useConsents = (props?: Props) => {
  const { fetchConsents = true } = props ?? {}
  const { locale } = useLocale()
  const [isLoading, setIsLoading] = useState(true)
  const [isSendingContent, setIsSendingConsent] = useState(false)
  const [data, setData] = useState<GetConsentsResponse['data'] | null>(null)
  const [slugs, setSlugs] = useState<Record<string, Consent>>({})

  // Fetch consents on mount
  useOnMount(() => {
    async function getConsents() {
      if (!fetchConsents) return
      setIsLoading(true)
      const consentsResponse = await ServerActions.serial.getConsents({ locale: locale.code })
      setData(consentsResponse.data)
      setSlugs(
        consentsResponse.data.consents.reduce<typeof slugs>((slugs, consent) => {
          slugs[consent.slug] = consent
          return slugs
        }, {})
      )
      setIsLoading(false)
    }
    void getConsents()
  })

  // Send a consent response
  const sendConsent = useCallback(
    async function sendConsent(args: { email: string; slug: string; value: boolean }) {
      const { email, slug, value } = args
      setIsSendingConsent(true)
      const response = await ServerActions.serial.sendConsents({
        locale: locale.code,
        data: { email, consentResponses: [{ slug, value }] },
      })
      setIsSendingConsent(false)
      return response
    },
    [locale.code]
  )

  return { data, slugs, sendConsent, isLoading, isSendingContent }
}
